import { Button } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { useUpdateProjectBudgetMutation } from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { toApiDate } from "@src/utils/dates";
import { type FieldState, FormState } from "formstate";
import { observer } from "mobx-react-lite";
import type { ExpenseCategory, OurWorkCategory } from "../models";
import type { TProject } from "../types";

interface SaveProps {
  projectId: string;
  ourWork: OurWorkCategory[];
  expenses: ExpenseCategory[];
  validFrom: FieldState<Date | undefined>;
  onCompleted: (val: TProject) => void;
  onInvalid: () => void;
}

const Save = ({
  projectId,
  ourWork,
  expenses,
  validFrom,
  onCompleted,
  onInvalid,
}: SaveProps) => {
  const [update, { loading }] = useUpdateProjectBudgetMutation({
    onCompleted(result) {
      if (result.updateProjectBudget) {
        onCompleted(result.updateProjectBudget);
      }
    },
    ...onError(),
  });

  const onClick = async () => {
    const { hasError } = await validate({ ourWork, expenses, validFrom });
    if (hasError) {
      onInvalid();
      return;
    }

    update({
      variables: {
        input: {
          project_id: projectId,
          budget_valid_from: validFrom.value
            ? toApiDate(validFrom.value)
            : undefined,
          ourWork: ourWork.flatMap((i) =>
            i.budgetItems.map(([_, j]) => j.serialize()),
          ),
          expenses: expenses.flatMap((i) =>
            i.budgetItems.map(([_, j]) => j.serialize()),
          ),
        },
      },
    });
  };

  return (
    <Button isLoading={loading} onClick={onClick}>
      {t`Save`}
    </Button>
  );
};

const validate = ({
  ourWork,
  expenses,
  validFrom,
}: Pick<SaveProps, "ourWork" | "expenses" | "validFrom">) => {
  const form = new FormState([
    ...ourWork.flatMap((i) => i.budgetItems.map(([_, j]) => j._form)),
    ...expenses.flatMap((i) => i.budgetItems.map(([_, j]) => j._form)),
    validFrom,
  ]);
  return form.validate();
};

export default observer(Save);
